<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <deleteConfirmationModal
            v-if="showDeleteConfirmationModal"
            :delete-function="deleteBankAccount"
            title="Delete bank account"
            message="Are you sure you want to delete this bank account?"
            :record-label="selectedBankAccount ? selectedBankAccount.accountNumber : ''"
            @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
            @refresh-records="getBankAccounts()"
          />
          <bankAccountModal
            v-if="showBankAccountModal"
            :bank-account="selectedBankAccount"
            @close-bank-account-modal="showBankAccountModal = false"
            @refresh-bank-accounts="getBankAccounts()"
          />

          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Bank Accounts</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <button type="button" class="list-page__action" v-on:click="openAddModal">
                <i class="pi pi-plus"></i>
                <span>New Bank Account</span>
              </button>
            </header>

            <div class="list-page__toolbar">
              <div class="list-search">
                <i class="pi pi-search"></i>
                <input
                  v-model="searchTerm"
                  type="text"
                  placeholder="Search label, account name or number…"
                />
              </div>
              <div class="list-page__toolbar-right">
                <select class="filter-select" v-model="bankFilter" v-on:change="onFilterChange">
                  <option value="">All banks</option>
                  <option v-for="bank in banks" :key="bank.value" :value="bank.value">{{ bank.label }}</option>
                </select>
                <button v-if="hasActiveFilters" type="button" class="list-clear-btn" v-on:click="clearFilters">
                  <i class="pi pi-times"></i>
                  Clear
                </button>
              </div>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="bankAccountsIsLoading" message="Loading bank accounts…" />
              <div v-if="!bankAccountsIsLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Label</th>
                      <th scope="col">Bank</th>
                      <th scope="col">Account Name</th>
                      <th scope="col">Account Number</th>
                      <th scope="col">Status</th>
                      <th scope="col">Created</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(bankAccount, index) in filteredBankAccounts"
                      v-bind:key="bankAccount.id || index"
                    >
                      <th scope="row">{{ index + 1 }}</th>
                      <td class="row-name">{{ bankAccount.name || "—" }}</td>
                      <td><span class="badge badge-secondary">{{ bankLabel(bankAccount.bank) }}</span></td>
                      <td>{{ bankAccount.accountName || "—" }}</td>
                      <td><span class="acct-cell">{{ bankAccount.accountNumber }}</span></td>
                      <td>
                        <span v-if="bankAccount.isActive" class="badge badge-success">Active</span>
                        <span v-else class="badge badge-danger">Inactive</span>
                      </td>
                      <td>{{ $filters.getDateOnly(bankAccount.createdAt) }}</td>
                      <td class="col-actions">
                        <div class="dropdown dropleft">
                          <div class="dropdown-toggle actions-dropdown" type="button" data-toggle="dropdown">
                            <i class="pi pi-ellipsis-h"></i>
                          </div>
                          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                            <a class="dropdown-item" v-on:click="openEditModal(bankAccount)">Edit</a>
                            <a class="dropdown-item" v-on:click="toggleIsActive(bankAccount)">
                              {{ bankAccount.isActive ? "Deactivate" : "Activate" }}
                            </a>
                            <a class="dropdown-item final-item text-danger" v-on:click="toggleDeleteConfirmationModal(bankAccount)">Delete</a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                v-if="!bankAccountsIsLoading && (!filteredBankAccounts || filteredBankAccounts.length === 0)"
                class="list-page__empty"
              >
                <i class="pi pi-building-columns"></i>
                <div v-if="searchTerm">No bank accounts match your search.</div>
                <div v-else>No bank accounts found.</div>
              </div>
            </div>

            <Pagination
              :metaData="bankAccountsMetaData"
              v-model:currentPage="currentPage"
              v-model:perPage="perPage"
              v-on:change="onPaginationChange"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import Pagination from "@/components/Pagination.vue";
import tableLoader from "@/components/Table_Loader.vue";
import deleteConfirmationModal from "@/components/Delete_Confirmation_Modal.vue";
import bankAccountModal from "@/components/bank_account/Bank_Account_Modal.vue";
import {
  DELETE_BANK_ACCOUNT,
  GET_BANK_ACCOUNTS,
  TOGGLE_BANK_ACCOUNT_IS_ACTIVE,
} from "@/store/action_types";
import { BANKS, BANK_LABELS } from "@/utils/enums";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";

export default {
  title: "Bank Accounts",
  components: {
    sidenav,
    navbar,
    Pagination,
    tableLoader,
    deleteConfirmationModal,
    bankAccountModal,
  },
  data() {
    return {
      banks: [
        { label: BANK_LABELS[BANKS.cbe], value: BANKS.cbe },
        { label: BANK_LABELS[BANKS.telebirr], value: BANKS.telebirr },
        { label: BANK_LABELS[BANKS.boa], value: BANKS.boa },
      ],
      bankFilter: "",
      searchTerm: "",
      currentPage: 1,
      perPage: 30,
      selectedBankAccount: null,
      showBankAccountModal: false,
      showDeleteConfirmationModal: false,
    };
  },
  computed: {
    bankAccounts() {
      return this.$store.getters.bankAccounts.data;
    },
    bankAccountsIsLoading() {
      return this.$store.getters.bankAccounts.isLoading;
    },
    bankAccountsMetaData() {
      return this.$store.getters.bankAccounts.metaData;
    },
    // Client-side text filter over the loaded page (the API has no free-text search).
    filteredBankAccounts() {
      const list = this.bankAccounts || [];
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return list;
      return list.filter((a) => {
        const haystack = [
          a.name,
          a.accountName,
          a.accountNumber,
          this.bankLabel(a.bank),
        ]
          .filter((value) => value != null)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    hasActiveFilters() {
      return !!(this.bankFilter || this.searchTerm);
    },
    countLabel() {
      const total = this.bankAccountsMetaData?.totalRecords ?? (this.bankAccounts || []).length;
      return total === 1 ? "1 bank account" : `${total} bank accounts`;
    },
  },
  methods: {
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    openAddModal() {
      this.selectedBankAccount = null;
      this.showBankAccountModal = true;
    },
    openEditModal(bankAccount) {
      this.selectedBankAccount = bankAccount;
      this.showBankAccountModal = true;
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getBankAccounts();
    },
    clearFilters() {
      this.bankFilter = "";
      this.searchTerm = "";
      this.currentPage = 1;
      this.getBankAccounts();
    },
    onPaginationChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.getBankAccounts();
    },
    toggleIsActive(bankAccount) {
      this.$store
        .dispatch(TOGGLE_BANK_ACCOUNT_IS_ACTIVE, { id: bankAccount.id })
        .then((res) => {
          if (res) {
            this.$toast.success(
              bankAccount.isActive ? "Bank account deactivated." : "Bank account activated.",
              TOAST_GLOBAL_CONFIG,
            );
          }
        });
    },
    toggleDeleteConfirmationModal(bankAccount) {
      this.selectedBankAccount = bankAccount;
      this.showDeleteConfirmationModal = true;
    },
    deleteBankAccount() {
      return this.$store.dispatch(DELETE_BANK_ACCOUNT, this.selectedBankAccount.id);
    },
    getBankAccounts() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      };
      if (this.bankFilter) params.bank = this.bankFilter;
      this.$store.dispatch(GET_BANK_ACCOUNTS, params);
    },
  },
  mounted() {
    this.getBankAccounts();
  },
};
</script>
<style scoped>
.filter-select {
  height: 36px !important;
  padding: 0 12px !important;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
  color: var(--ehs-color-ink);
  font-size: 0.78rem;
  font-family: inherit;
  outline: none;
  box-shadow: var(--ehs-shadow-xs);
}

.filter-select:focus {
  border-color: var(--ehs-color-primary);
  box-shadow: var(--ehs-shadow-focus);
}

.acct-cell {
  font-family: var(--ehs-font-mono);
  font-size: 0.8rem;
  color: var(--ehs-color-text);
}

@media (max-width: 991.98px) {
  /* Give the search its own row so the bank select + clear don't squeeze it
     out — the shared "one row" rule assumes a lighter toolbar. */
  .list-page__toolbar {
    flex-wrap: wrap;
  }

  .list-search {
    flex: 1 1 100%;
  }

  .list-page__toolbar-right {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .filter-select {
    flex: 1 1 auto;
  }
}
</style>
